/**
 * MarkSentDialog — quote status manual override (2026-07-24).
 *
 * Records that an internally-approved quote was sent to the client OUTSIDE the
 * system (emailed from Outlook, printed, handed over). On success the quote
 * flips to 'Sent' with the same side effects as the real Send button: SentAt
 * stamped and the FX rate locked. The QuoteEvent notes the channel so the
 * activity log distinguishes a manual send from a system-emailed one.
 */
import { useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { markQuoteSent, type MarkSentChannel } from '../../../services/quoteService';

/** Single source for the channel options — no magic strings in the JSX. */
const CHANNEL_OPTIONS: ReadonlyArray<{ value: MarkSentChannel; label: string }> = [
  { value: 'email', label: 'Email (outside the system)' },
  { value: 'printed', label: 'Printed / handed over' },
  { value: 'other', label: 'Other' },
];

interface Props {
  open: boolean;
  quoteKey: string;
  onClose: () => void;
  /** Called after a successful mark-sent — the parent refetches the quote. */
  onSent: () => void;
}

export const MarkSentDialog = ({ open, quoteKey, onClose, onSent }: Props) => {
  const [channel, setChannel] = useState<MarkSentChannel>('email');
  const [sentTo, setSentTo] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setChannel('email'); setSentTo(''); setNote('');
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await markQuoteSent(quoteKey, {
        channel,
        sentTo: sentTo.trim() || undefined,
        note: note.trim() || undefined,
      });
      reset();
      onSent();
    } catch (err) {
      const resp = (err as { response?: { data?: { error?: string } } }).response;
      setError(resp?.data?.error
        ?? (err instanceof Error ? err.message : 'Failed to mark the quote sent.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Mark Quote as Sent</DialogTitle>
      <DialogContent>
        <TextField label="How was it sent?" fullWidth required select size="small" autoFocus
          value={channel}
          onChange={(e) => setChannel(e.target.value as MarkSentChannel)}
          sx={{ mt: 1 }}>
          {CHANNEL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField label="Sent to (optional)" fullWidth size="small"
          value={sentTo} onChange={(e) => setSentTo(e.target.value)} sx={{ mt: 2 }}
          placeholder="e.g. j.smith@client.com" />
        <TextField label="Note (optional)" fullWidth multiline minRows={2} size="small"
          value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 2 }}
          placeholder="e.g. sent from Outlook with revised cover letter" />
        <Alert severity="info" sx={{ mt: 2 }}>
          The exchange rate is locked on save, exactly as if the quote had been
          emailed from the system.
        </Alert>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
          {submitting ? <CircularProgress size={18} /> : 'Mark as Sent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
